import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { updateUserWallet } from '../../store/slices/usersSlice';
import { CompleteUser, WalletUpdateRequest } from '../../types';
import { toast } from 'react-toastify';

const schema = yup.object().shape({
  phoneNumber: yup.string().required('Phone number is required'),
  amount: yup.number().positive('Amount must be positive').required('Amount is required'),
});

type FormData = {
  phoneNumber: string;
  amount: number;
};

interface WalletUpdateModalProps {
  user: CompleteUser | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const WalletUpdateModal: React.FC<WalletUpdateModalProps> = ({
  user,
  open,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      phoneNumber: user?.phoneNumber || '',
      amount: 0,
    },
  });

  React.useEffect(() => {
    if (open && user) {
      reset({
        phoneNumber: user.phoneNumber || '',
        amount: 0,
      });
    }
  }, [open, user, reset]);

  const onSubmit = async (data: FormData) => {
    if (!user) return;

    setLoading(true);
    try {
      const walletUpdateData: WalletUpdateRequest = {
        phoneNumber: data.phoneNumber,
        amount: data.amount,
      };

      const result = await dispatch(updateUserWallet(walletUpdateData) as any);
      
      if (result.payload) {
        toast.success('Wallet balance updated successfully');
        onSuccess();
        onClose();
      } else {
        toast.error('Failed to update wallet balance');
      }
    } catch (error) {
      toast.error('An error occurred while updating wallet balance');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
    }).format(amount);
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        <Typography variant="h6">Add Balance to Wallet</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Adding balance for user: <strong>{user.username}</strong>
            <br />
            Current Balance: <strong>{formatCurrency(user.wallet)}</strong>
          </Alert>
          
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Phone Number"
                placeholder="251912169284"
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber?.message}
                sx={{ mb: 2 }}
              />
            )}
          />

          <Controller
            name="amount"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Amount to Add"
                type="number"
                placeholder="100"
                error={!!errors.amount}
                helperText={errors.amount?.message}
                sx={{ mb: 2 }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? 'Updating...' : 'Add Balance'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WalletUpdateModal; 